import { FC, useMemo } from 'react'
import { Box, Link, Typography } from '@mui/material'
import { CheckCircle, FactCheckOutlined, RemoveRedEye } from '@mui/icons-material'
import { useIntl } from 'react-intl'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import Section from 'components/Section'
import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractsDepositAccountBalance } from 'services/contracts/hooks/useLeaseContractsDepositAccountBalance'
import { LeaseContractsBookingAgreementResponse } from 'services/contracts/hooks/useLeaseContractsBookingAgreement'
import OperationsFrozenMessage from 'components/OperationsFrozenMessage'
import AdditionalServicesOnlinePaymentButton from './AdditionalServicesOnlinePaymentButton'
import AdditionalServicesPaymentDetailsButton from './AdditionalServicesPaymentDetailsButton'

interface AdditionalServicesBookingAgreementLegacyProps {
  metadata?: LeaseContractsBookingAgreementResponse
  infoMessage?: string
}

const AdditionalServicesBookingAgreementLegacy: FC<AdditionalServicesBookingAgreementLegacyProps> = ({
  metadata,
  infoMessage
}) => {
  const { formatMessage } = useIntl()
  const { activeLeaseContractId } = useActiveLeaseContractId()
  const { leaseContractsDepositAccountBalance } =
    useLeaseContractsDepositAccountBalance(activeLeaseContractId)
  const { getActiveContract } = useLeaseContractsList()

  const activeContract = useMemo(
    () => getActiveContract(activeLeaseContractId),
    [activeLeaseContractId, getActiveContract]
  )

  const depositBalanceValue = useMemo(
    () => leaseContractsDepositAccountBalance?.depositBalanceValue || '0',
    [leaseContractsDepositAccountBalance]
  )

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'additionalServicesBookingAgreement.title',
          defaultMessage: 'Umowa rezerwacyjna'
        })}
      />
      <BackNavigationContent>
        <Section>
          <Box mt={2} sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center'
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                <FactCheckOutlined color='disabled' />
                <Typography variant='h6' fontWeight={600} ml={1}>
                  {formatMessage({
                    id: 'additionalServicesBookingAgreement.title',
                    defaultMessage: 'Umowa rezerwacyjna'
                  })}
                </Typography>
              </Box>
              {metadata?.bookingAgreementPdf && (
                <Link
                  href={metadata.bookingAgreementPdf}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                  <RemoveRedEye />
                </Link>
              )}
            </Box>
            {!metadata?.bookingAgreementPdf && (
              <Typography sx={{ mt: 1, color: 'text.secondary' }} variant='body2'>
                {formatMessage({
                  id: 'additionalServicesBookingAgreement.noDocument',
                  defaultMessage:
                    'Obecnie umowa nie jest dostępna, skontaktuj się ze swoim opiekunem'
                })}
              </Typography>
            )}

            {infoMessage && (
              <Typography sx={{ mt: 2, color: 'text.secondary' }} variant='body2'>
                {infoMessage}
              </Typography>
            )}

            {Number(depositBalanceValue) < 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    alignItems: 'center',
                    mt: 3,
                    mb: 2
                  }}>
                  <Typography
                    variant='overline'
                    fontSize={14}
                    fontWeight={700}
                    ml={1}
                    color='primary'>
                    {formatMessage({
                      id: 'additionalServicesBookingAgreement.depositToPay',
                      defaultMessage: 'Kaucja do wpłacenia'
                    })}
                  </Typography>
                  <Typography variant='h6' fontWeight={600} ml={1}>
                    {`${depositBalanceValue} ${activeContract?.paymentCurrency}`}
                  </Typography>
                </Box>
                {activeContract?.isOperationsFrozen ? (
                  <OperationsFrozenMessage />
                ) : (
                  <>
                    {activeContract?.paymentCurrency &&
                      ['PLN'].includes(activeContract.paymentCurrency) && (
                        <AdditionalServicesOnlinePaymentButton
                          documentType='deposit'
                          documentId={activeLeaseContractId}
                          variant='contained'
                          fullWidth
                        />
                      )}
                    <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
                      <AdditionalServicesPaymentDetailsButton
                        amount={depositBalanceValue}
                        paidAmount={'0'}
                        type='deposit'
                      />
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Section
                sx={{
                  mt: 2,
                  py: 4,
                  borderColor: '#EDFFFA',
                  backgroundColor: '#EDFFFA'
                }}>
                <Box>
                  <Typography variant='h5' fontWeight={600}>
                    {formatMessage({
                      id: 'additionalServicesPaymentStatus.paid',
                      defaultMessage: 'Opłacone'
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '70px',
                    height: '70px',
                    backgroundColor: '#D7FFF4',
                    borderRadius: '50%',
                    mr: 2
                  }}>
                  <CheckCircle sx={{ color: '#00B383' }} />
                </Box>
              </Section>
            )}
          </Box>
        </Section>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesBookingAgreementLegacy
